import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { get } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import "./form.css";
import {
  FormAction,
  OnboardingApplicationStatus,
  OnboardingFlow,
  OnboardingFormModuleName,
  RoleEnum,
} from "../../types";
import {
  fetchCandidateOnboardingFormAsync,
  fetchConfigAsync,
  fetchOnboardingFormTemplateAsync,
  reset,
} from "../../redux/onboarding-slice";
import {
  selectCandidateOnboardingForm,
  selectCandidateOnboardingFormValues,
  selectOnboardingFormTemplate,
  selectDataError,
  selectCompletedSectionLength,
  selectTotalSectionLength,
  selectOnboardingFlow,
  selectOnboardingCandidateName,
} from "../../redux/selector";
import { SurveyComponent } from "../../components/surveyjs/survey/survey.component";
import { useLocation, useNavigate } from "react-router-dom";
import { HeaderComponent } from "../../../../components/header/header.component";
import { ErrorScreen } from "../../../../components/error-screen/error-screen";
import { color } from "../../../../mui-theme/palette";
import { useQuery } from "../../../../utils/hooks/use-query";
import ProgressBar from "../../../../components/progress-bar/progress-bar";
import { backToApp, checkCameraPermission } from "../../utils";
import { confirm } from "../../../../components/modal/comfirm";
import { useTranslation } from "react-i18next";
import { ONBOARDING_PATH, ROOT_PATH } from "../../../../routes/constants";
import { onboardingExcludedMenuModules } from "../../../../constants/constants";
import { GlobalHelper } from "../../../../utils/helpers/global-helper";
import NavigatorCard from "../../../../components/navigator-card/navigator-card";
import { getToken, parseJwt } from "../../utils";
import { useCanGoBack } from "../../../../utils/hooks/use-can-go-back";
import { getOnboardingModuleNameBySection, submitOnboardingForm } from "./util";
import useThrottledTouchEnd from "../../hooks/use-throtted-touch-end";
import { convertInputMaskPattern } from "./survey.util";

export let preQuestionsData: any = {};
const individualHeaderModuleOrSectionNames = [OnboardingFormModuleName.ESignature];

const isShowNavigatorCard = (formCreatorUserId?: string) => {
  const rawToken = getToken();
  const tokenData = rawToken ? parseJwt(rawToken) : {};
  return !(tokenData.sub === formCreatorUserId);
};

export const SurveyFormScreen: React.FC = () => {
  useThrottledTouchEnd();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const [loading, setLoading] = useState(true);
  const query = useQuery();
  const candidateId = query.get("candidateId");

  const candidateOnboardingForm = useSelector(selectCandidateOnboardingForm);
  const candidateOnboardingFormValues = useSelector(selectCandidateOnboardingFormValues);
  const candidateName = useSelector(selectOnboardingCandidateName);
  const formTemplate = useSelector(selectOnboardingFormTemplate);
  const dataError = useSelector(selectDataError);
  const onboardingFlow = useSelector(selectOnboardingFlow);

  const totalSections = useSelector(selectTotalSectionLength);
  const compeletedSections = useSelector(selectCompletedSectionLength);
  const notificationActionId = query.get("notificationActionId") ?? "";

  useEffect(() => {
    if (window.history.length === 1) {
      // history lenght equal to 1 indicate this is the first route, should better to reset the state
      dispatch<any>(reset());
    }
  }, []);

  const initSectionName = useMemo(() => {
    const sectionName =
      get(location.state, "sectionName", query.get("sectionName")) ||
      OnboardingFormModuleName.PreQuestions;
    return sectionName;
  }, [location.state, query]);

  const [sectionName, setCurrentSectionName] = useState(initSectionName);

  const moduleName = useMemo(() => {
    return getOnboardingModuleNameBySection(formTemplate, sectionName, candidateId as string);
  }, [sectionName, formTemplate, candidateId]);

  const headerText = useMemo(() => {
    // default application title
    let title = t("Recruitment.onboarding_application");
    if (individualHeaderModuleOrSectionNames.includes(sectionName)) {
      title = t(`Recruitment.${sectionName}`);
    } else if (individualHeaderModuleOrSectionNames.includes(moduleName)) {
      title = t(`Recruitment.${moduleName}`);
    }
    return title;
  }, [sectionName, moduleName, t]);

  const submitForm = useCallback(async (data: any, formAction: FormAction) => {
    var data = await submitOnboardingForm({
      data,
      dispatch,
      formAction,
      notificationActionId,
    });
    return data;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    if (!candidateId) return setLoading(false);
    let form: any;
    const moduleName = GlobalHelper.getOnboardingCurrentSection()?.moduleName;
    if (candidateOnboardingForm?.candidateId !== candidateId) {
      form = await dispatch<any>(fetchCandidateOnboardingFormAsync({ candidateId }));
      if (get(form, "error", false)) return setLoading(false);
    } else {
      if (moduleName === OnboardingFormModuleName.PreQuestions) {
        navigate(`${ROOT_PATH}/${ONBOARDING_PATH}?${query.toString()}`);
      }
    }

    const formInstance = form ? get(form, "payload.data[0]", undefined) : candidateOnboardingForm;
    await dispatch<any>(
      fetchOnboardingFormTemplateAsync(
        formInstance.templateId
          ? { templateId: formInstance.templateId, candidateId }
          : { candidateId, type: query.get("type") },
      ),
    );
    setLoading(false);
  }, [candidateId, candidateOnboardingForm, dispatch, navigate, query]);
  useEffect(() => {
    if (!onboardingFlow || !candidateOnboardingForm) return;
    checkCameraPermission(candidateOnboardingForm, onboardingFlow);
  }, [onboardingFlow, candidateOnboardingForm]);

  useEffect(() => {
    if (!onboardingFlow) {
      dispatch<any>(fetchConfigAsync());
      return;
    }
    fetchData();
  }, [onboardingFlow]);

  const surveyJson = useMemo(() => {
    if (formTemplate) {
      return convertInputMaskPattern(formTemplate);
    }
    return "";
  }, [formTemplate]);

  const surveyUI = useMemo(() => {
    if (dataError) {
      return <ErrorScreen statusCode="403" />;
    }
    if (loading) {
      return (
        <div className={`w-screen h-screen flex flex-1 items-center justify-center`}>
          <CircularProgress sx={{ color: color.majorRed }} />
        </div>
      );
    }

    return surveyJson && sectionName ? (
      <div className="flex flex-col">
        <SurveyComponent
          currentSectionName={sectionName}
          setCurrentSectionName={setCurrentSectionName}
          json={surveyJson}
          defaultValue={candidateOnboardingFormValues}
          onSubmit={submitForm}
          npaNumber={candidateOnboardingForm?.npaNumber}
        />
      </div>
    ) : null;
  }, [loading, dataError, surveyJson, sectionName, candidateOnboardingFormValues, submitForm]);

  const goBack = useCallback(() => {
    const moduleName = GlobalHelper.getOnboardingCurrentSection()?.moduleName;
    if (moduleName === OnboardingFormModuleName.PreQuestions) {
      confirm({
        message: t("confirm_leave_form_warning"),
      }).then((result) => {
        if (result.ok) {
          backToApp();
        }
      });
    } else {
      if (canGoBack()) {
        navigate(`${ROOT_PATH}/${ONBOARDING_PATH}?${query.toString()}`);
      }
    }
  }, []);

  const pageBanner = useMemo(() => {
    const moduleName = GlobalHelper.getOnboardingCurrentSection()?.moduleName;
    return (
      <div>
        {!onboardingExcludedMenuModules.includes(moduleName) &&
          candidateOnboardingForm?.applicationStatus === OnboardingApplicationStatus.EDITING && (
            <div>
              <ProgressBar
                className="px-4"
                progress={compeletedSections / totalSections}
                text={`(${compeletedSections
                  .toString()
                  .padStart(totalSections.toString().length, "0")}/${totalSections})`}
              />
            </div>
          )}
        {individualHeaderModuleOrSectionNames.includes(moduleName) &&
          isShowNavigatorCard(candidateOnboardingForm?.formCreatorUserId) && (
            <NavigatorCard
              applicationNumber={candidateOnboardingForm?.applicationId}
              candidateName={candidateName}
            />
          )}
      </div>
    );
  }, [compeletedSections, totalSections, candidateName, candidateOnboardingForm]);

  return (
    <div className="h-screen w-screen flex flex-col">
      <HeaderComponent title={headerText} onBack={goBack} />
      {pageBanner}
      <div className="flex justify-center flex-1">{surveyUI}</div>
    </div>
  );
};
